/* eslint-disable react-hooks/set-state-in-effect */
import type {
  DigitalProofingAndArtworkFormProps,
  DigitalProofingProduct,
  SweetAlertProps
} from './DigitalProofingAndArtworkForm.types'
import type {
  ArtworkOption,
  ArtworkOverlayGroup,
  ArtworkOverlayOption,
  OverlaySelectionsByGroup
} from './digitalProofingArtworkTypes'
import type { DynamicFieldValues } from './dynamicProofingFormTypes'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import brandit_rails from '../../../api/brandit_rails'
import { getDigitalProofPreview } from '../../../util/digitalProofPreview.js'
import { useDigitalProofPreview } from '../../../v2/digital-proofing/hooks/useDigitalProofPreview'
import {
  filterOverlayGroupsByDependencyVisibility,
  resolveArtworkElementDependencyVisibility
} from './artworkElementDependencyVisibility'
import {
  NO_SELECTION_ID,
  cloneFormData,
  findSelection,
  normalizedSelectionId,
  overlayGroupId,
  overlaySelectionsEqual,
  overlayGroupSelectionValid,
  sanitizeOverlaySelections,
  visibleOverlayGroupsForArtwork
} from './digitalProofingArtworkFormUtils'

const appendFormValue = (formData: FormData, key: string, value?: number | string | null) => {
  formData.append(key, value ? String(value) : '')
}

const selectBundleArtworks = (product: DigitalProofingProduct) => product.nested_data?.[0]?.nested_data ?? []

const selectProductArtworks = (product: DigitalProofingProduct) => {
  if (product.is_digital_proofing || product.flags?.is_digital_proofing) {
    return product.digitalProofing?.nested_data ?? []
  }

  if (product.flags?.is_artwork) {
    return product.artwork?.nested_data ?? []
  }

  return []
}

export const useDigitalProofingAndArtworkForm = ({
  product,
  quantity,
  isBundleProduct,
  addProductToBasket,
  setSelectedArtwork,
  setSelectedCoupon,
  setSelectedAddOn,
  setSelectedAddOn2,
  collapseDpForm,
  selectedArtwork,
  currentUser,
  portal,
  customerGroup,
  updateDigitalProofingSelection,
  dispatchSweetAlert,
  clearSweetAlert,
  fetchAddressesByPortalConfig
}: DigitalProofingAndArtworkFormProps) => {
  const [, setLoading] = useState(false)
  const [artworks, setArtworks] = useState<ArtworkOption[]>([])
  const [dynamicFieldValues, setDynamicFieldValues] = useState<DynamicFieldValues>({})
  const [selectedOverlayIdsByGroup, setSelectedOverlayIdsByGroup] = useState<OverlaySelectionsByGroup>({})
  const [selectedArtworkId, setSelectedArtworkId] = useState(NO_SELECTION_ID)
  const [showProofModal, setShowProofModal] = useState(false)
  const [proof1, setProof1] = useState(false)
  const [proof2, setProof2] = useState(false)
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const skipProofReopenRef = useRef(false)
  const isAddingToCartRef = useRef(false)
  const retryData = useRef<FormData | null>(null)
  const cancelAlertPendingRef = useRef(false)
  const { proofingPreview, submitPreview, cancelPreview, resetPreview, isSubmitting, isGenerating, isFailed, error } =
    useDigitalProofPreview()
  const isDigitalProofingProduct = !!(product.is_digital_proofing || product.flags?.is_digital_proofing)
  const selectedOverlayIds = useMemo(
    () =>
      Object.values(selectedOverlayIdsByGroup)
        .flat()
        .filter((id): id is string => Boolean(id) && id !== NO_SELECTION_ID),
    [selectedOverlayIdsByGroup]
  )
  const baseOverlayGroups = useMemo(() => visibleOverlayGroupsForArtwork(selectedArtwork), [selectedArtwork])
  const dependencyVisibility = useMemo(
    () =>
      resolveArtworkElementDependencyVisibility({
        artwork: selectedArtwork || null,
        fieldValues: dynamicFieldValues,
        selectedArtworkOverlayIds: selectedOverlayIds
      }),
    [dynamicFieldValues, selectedArtwork, selectedOverlayIds]
  )
  const visibleOverlayGroups = useMemo(
    () => filterOverlayGroupsByDependencyVisibility(baseOverlayGroups, dependencyVisibility),
    [baseOverlayGroups, dependencyVisibility]
  )
  const overlayOptionsById = useMemo(() => {
    const optionsById: Record<string, ArtworkOverlayOption> = {}
    visibleOverlayGroups.forEach(group => {
      group.artwork_overlays.forEach(option => {
        optionsById[normalizedSelectionId(option)] = option
      })
    })
    return optionsById
  }, [visibleOverlayGroups])
  const selectedVisibleOverlayIds = useMemo(
    () => selectedOverlayIds.filter(id => Boolean(overlayOptionsById[id])),
    [overlayOptionsById, selectedOverlayIds]
  )
  const selectedOverlayOptions = useMemo(
    () =>
      selectedVisibleOverlayIds
        .map(id => overlayOptionsById[id])
        .filter((option): option is ArtworkOverlayOption => Boolean(option)),
    [overlayOptionsById, selectedVisibleOverlayIds]
  )
  const selectedCouponOverlay = useMemo(
    () => selectedOverlayOptions.find(option => option.overlay_type === 'coupon'),
    [selectedOverlayOptions]
  )
  const selectedAddOnOverlays = useMemo(
    () => selectedOverlayOptions.filter(option => option.overlay_type === 'add_on'),
    [selectedOverlayOptions]
  )
  const hasRenderingCoupon = useMemo(
    () => (selectedCouponOverlay ? selectedCouponOverlay.renders : undefined),
    [selectedCouponOverlay]
  )
  const overlayGroupsValid = useMemo(
    () => visibleOverlayGroups.every(group => overlayGroupSelectionValid(group, selectedOverlayIdsByGroup)),
    [selectedOverlayIdsByGroup, visibleOverlayGroups]
  )
  const selectedArtworkKey = selectedArtwork ? selectedArtwork.artwork_id : undefined
  const previewLoading = isSubmitting || isGenerating
  const previewFailed = isFailed
  const previewErrorMessage = error?.message || null
  const proofPreview = useMemo(() => getDigitalProofPreview(proofingPreview), [proofingPreview])
  const proofingPreviewId = proofingPreview?.id

  const resetAllSelections = useCallback(() => {
    setSelectedArtworkId(NO_SELECTION_ID)
    setSelectedOverlayIdsByGroup({})
    setProof1(false)
    setProof2(false)
  }, [])

  const resetAddingToCart = useCallback(() => {
    isAddingToCartRef.current = false
    setIsAddingToCart(false)
  }, [])

  const addProductCallback = () => {
    cancelAlertPendingRef.current = false
    resetAddingToCart()
    resetPreview()
    retryData.current = null
    setShowProofModal(false)
    resetAllSelections()
  }

  const downloadProofPreview = () => {
    if (proofPreview?.url) {
      window.open(proofPreview.url)
    }
  }

  useEffect(() => {
    resetPreview()
    return () => {
      cancelPreview()
      retryData.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const artwork = findSelection(artworks, selectedArtworkId)
    setSelectedArtwork?.(artwork || false)
  }, [artworks, selectedArtworkId, setSelectedArtwork])

  useEffect(() => {
    setSelectedCoupon?.(selectedCouponOverlay || false)
    setSelectedAddOn?.(selectedAddOnOverlays[0] || false)
    setSelectedAddOn2?.(selectedAddOnOverlays[1] || false)
  }, [selectedCouponOverlay, selectedAddOnOverlays, setSelectedCoupon, setSelectedAddOn, setSelectedAddOn2])

  useEffect(() => {
    const userId = Number.parseInt(String(currentUser.id), 10)

    if (!userId) return

    setLoading(true)
    fetchAddressesByPortalConfig(portal).then(() => setLoading(false))
  }, [currentUser.id, fetchAddressesByPortalConfig, portal])

  useEffect(() => {
    if (!product.product_id) return

    if (isBundleProduct === true) {
      setArtworks(selectBundleArtworks(product).filter(artwork => artwork.pdf_type === 'Artwork'))
    } else if (customerGroup.id) {
      const hiddenArtworkList = customerGroup.hidden_artworks_list || []
      const hiddenArtworkIds = hiddenArtworkList.map(hiddenArtwork => String(hiddenArtwork.id))
      const productArtworks = selectProductArtworks(product)
      const notHiddenArtworks = productArtworks.filter(artwork => {
        const artworkIds = [artwork.artwork_id, artwork.id].filter(id => id !== null && id !== undefined).map(String)
        return !artworkIds.some(id => hiddenArtworkIds.includes(id))
      })
      setArtworks(notHiddenArtworks.filter(artwork => artwork.pdf_type === 'Artwork'))
    }
  }, [customerGroup, isBundleProduct, product])

  useEffect(() => {
    setDynamicFieldValues({})
  }, [selectedArtworkKey])

  useEffect(() => {
    setSelectedOverlayIdsByGroup(currentSelections => {
      const sanitizedSelections = sanitizeOverlaySelections(visibleOverlayGroups, currentSelections)
      return overlaySelectionsEqual(currentSelections, sanitizedSelections) ? currentSelections : sanitizedSelections
    })
  }, [visibleOverlayGroups])

  useEffect(() => {
    if (artworks.length === 1) {
      setSelectedArtworkId(normalizedSelectionId(artworks[0]))
    }
  }, [artworks])

  const handleAddProduct = () => {
    if (isAddingToCartRef.current) return
    isAddingToCartRef.current = true
    setIsAddingToCart(true)

    if (isBundleProduct) {
      updateDigitalProofingSelection({
        bundleProduct: product,
        proofingPreview
      })
        .then(() => {
          cancelAlertPendingRef.current = false
          resetAddingToCart()
          resetPreview()
          retryData.current = null
          setShowProofModal(false)
          collapseDpForm?.(product.bundle_group_product_id)
          resetAllSelections()
        })
        .catch(resetAddingToCart)
    } else {
      const addToCartPromise = addProductToBasket?.(product, quantity, proofingPreview, addProductCallback, () =>
        setShowProofModal(false)
      )

      if (addToCartPromise) {
        addToCartPromise.catch(resetAddingToCart)
      } else {
        resetAddingToCart()
      }
    }
  }

  const openPublishModal = () => {
    if (!proofingPreviewId) return
    setShowProofModal(false)
    setPublishModalOpen(true)
  }

  const handlePublishModalOpenChange = (open: boolean) => {
    setPublishModalOpen(open)
    if (!open) {
      if (skipProofReopenRef.current) {
        skipProofReopenRef.current = false
        return
      }
      if (proofingPreviewId) {
        setShowProofModal(true)
      }
    }
  }

  const finishPublishCleanup = () => {
    skipProofReopenRef.current = true
    cancelAlertPendingRef.current = false
    resetAddingToCart()
    resetPreview()
    retryData.current = null
    setShowProofModal(false)
    setPublishModalOpen(false)
    resetAllSelections()
  }

  const handlePublishSuccess = () => {
    skipProofReopenRef.current = true
    cancelAlertPendingRef.current = false
    resetAddingToCart()
    setShowProofModal(false)
    setPublishModalOpen(false)

    dispatchSweetAlert({
      alertMessage: 'Your post was published successfully.',
      type: 'success',
      showCancel: true,
      confirmBtnText: 'Publish Again',
      cancelBtnText: 'Close',
      confirmBtnCssClass: 'mf-primary-btn alert-width-btn',
      cancelBtnCssClass: 'mf-outline-btn alert-width-btn',
      onConfirm: () => {
        if (!proofingPreviewId) {
          finishPublishCleanup()
          return
        }
        setPublishModalOpen(true)
      },
      onCancel: () => {
        clearSweetAlert()
        finishPublishCleanup()
      }
    })
  }

  const formComplete = (incomingFormData: FormData, isRetry = false) => {
    setShowProofModal(true)

    if (isRetry) {
      submitPreview(retryData.current || incomingFormData)
      return
    }

    appendFormValue(incomingFormData, 'product_id', isBundleProduct ? product.product_id : product.id)
    appendFormValue(
      incomingFormData,
      'artwork_id',
      isBundleProduct ? selectedArtworkId : selectedArtwork ? selectedArtwork.artwork_id : undefined
    )
    selectedVisibleOverlayIds.forEach(artworkOverlayId => {
      incomingFormData.append('artwork_overlay_ids[]', artworkOverlayId)
    })
    incomingFormData.append('approved_date', moment().toString())
    retryData.current = cloneFormData(incomingFormData)

    submitPreview(incomingFormData)
  }

  const cancelProofConfirmed = useCallback(() => {
    cancelAlertPendingRef.current = false
    resetAddingToCart()
    cancelPreview()
    setShowProofModal(false)
    resetAllSelections()
    retryData.current = null
    if (previewFailed || !proofingPreviewId) {
      return
    }
    brandit_rails.post(`/product/digital_proofing_products/destroy/${proofingPreviewId}`)
  }, [cancelPreview, previewFailed, proofingPreviewId, resetAddingToCart, resetAllSelections])

  const dismissCancelAlert = useCallback(() => {
    cancelAlertPendingRef.current = false
    clearSweetAlert()
  }, [clearSweetAlert])

  const sweetAlertProps = useMemo<SweetAlertProps>(
    () => ({
      title: 'Are you sure you want to cancel?',
      alertMessage: 'You will lose any selections you have made.',
      type: 'warning',
      showCancelButton: true,
      confirmBtnText: 'Yes, cancel',
      cancelBtnText: 'No, keep it',
      onConfirm: cancelProofConfirmed,
      onCancel: dismissCancelAlert
    }),
    [cancelProofConfirmed, dismissCancelAlert]
  )

  useEffect(() => {
    if (!cancelAlertPendingRef.current || !proofingPreviewId) return
    dispatchSweetAlert(sweetAlertProps)
  }, [dispatchSweetAlert, proofingPreviewId, sweetAlertProps])

  const cancelProof = () => {
    if (!previewLoading) {
      cancelProofConfirmed()
      return
    }

    cancelAlertPendingRef.current = true
    dispatchSweetAlert(sweetAlertProps)
  }

  const toggleProof = (num: 1 | 2) => {
    if (num === 1) {
      setProof1(!proof1)
    } else {
      setProof2(!proof2)
    }
  }

  const retryPreview = () => {
    if (retryData.current) {
      formComplete(retryData.current, true)
    }
  }

  const handleSelectOverlayGroup = (group: ArtworkOverlayGroup, selectedId: string) => {
    setSelectedOverlayIdsByGroup(currentSelections => ({
      ...currentSelections,
      [overlayGroupId(group)]: selectedId === NO_SELECTION_ID ? [] : [selectedId]
    }))
  }

  const handleToggleOverlayGroupOption = (group: ArtworkOverlayGroup, optionId: string, checked: boolean) => {
    setSelectedOverlayIdsByGroup(currentSelections => {
      const groupId = overlayGroupId(group)
      const selectedIds = currentSelections[groupId] ?? []
      const nextSelectedIds = checked ? [...selectedIds, optionId] : selectedIds.filter(id => id !== optionId)

      return {
        ...currentSelections,
        [groupId]: nextSelectedIds
      }
    })
  }

  return {
    artworks,
    formComplete,
    handleAddProduct,
    handlePublishModalOpenChange,
    handlePublishSuccess,
    handleSelectOverlayGroup,
    handleToggleOverlayGroupOption,
    hasRenderingCoupon,
    isDigitalProofingProduct,
    openPublishModal,
    overlayGroupsValid,
    overlayOptionsById,
    previewErrorMessage,
    previewFailed,
    previewLoading,
    proofingPreview,
    proofingPreviewId,
    proofPreview,
    publishModalOpen,
    setPublishModalOpen,
    isAddingToCart,
    isGeneratingProof: isGenerating,
    proof1,
    proof2,
    resetAllSelections,
    retryPreview,
    selectedArtworkId,
    selectedArtworkOverlayIds: selectedVisibleOverlayIds,
    selectedOverlayIdsByGroup,
    setSelectedArtworkId,
    setDynamicFieldValues,
    showProofModal,
    toggleProof,
    visibleOverlayGroups,
    cancelProof,
    downloadProofPreview
  }
}
